import React, { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks'
import SearchPanel from '../Components/SearchPanel'
import VocabularyPanel from '../Components/VocabularyPanel'
import { clearHighlights, listMyVocabularies, setCurrentVocabulary, setHighlights } from '../Store/VocabulariesSlice'
import './LandingPage.less'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

interface LandingPageParams {
  v?: string | number;
  highlight?: string;
  showSearch?: boolean | 'true' | 'false';
}

function useLandingPageParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  const v = searchParams.get('v')
  const highlight = searchParams.get('highlight')
  const showSearch = searchParams.get('showSearch')

  const updateParams = (newParams: LandingPageParams) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      Object.entries(newParams).forEach(([key, val]) => next.set(key, String(val)))
      return next
    })
  }

  return { v, highlight, showSearch, updateParams }
}

function LandingPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { v, highlight, showSearch, updateParams } = useLandingPageParams()

  const preferences = useAppSelector((state) => state.preferences.preferences)
  const vocabularies = useAppSelector((state) => state.vocabularies.vocabularies)
  const currentSearch = useAppSelector((state) => state.search.currentSearch)

  // 1. NEGOTIATION: Handle the "What do I show?" logic during render
  const activeVocabularyId = Number(v) || preferences?.currentVocabulary || vocabularies[0]?.id
  const isSearchShown = showSearch !== null ? (showSearch === 'true') : !!(currentSearch?.query)

  // 2. THE "BOOTSTRAP" EFFECT: Only runs when vital data is missing
  useEffect(() => {
    if (!preferences) return

    if (!v && activeVocabularyId) {
      // Redirect to the "correct" URL if the URL is currently empty
      updateParams({ v: activeVocabularyId, showSearch: isSearchShown })
    }

    // If no vocabularies exist at all, fetch them
    if (vocabularies.length === 0) {
      dispatch(listMyVocabularies())
    }
  }, [preferences, v, activeVocabularyId, vocabularies.length])

  // 3. THE LOADER
  // This watches the URL. Whenever 'v' actually changes, we fetch data.
  useEffect(() => {
    if (!v) return

    const loadData = async () => {
      const result = await dispatch(setCurrentVocabulary(Number(v)))

      if (setCurrentVocabulary.fulfilled.match(result)) {
        // Only handle highlights AFTER the vocabulary successfully loads
        if (highlight) {
          dispatch(setHighlights(decodeURIComponent(highlight)))
        } else {
          dispatch(clearHighlights())
        }
      } else {
        toast.error(t('ErrorLoadingVocabulary'))
      }
    }
    // noinspection JSIgnoredPromiseFromCall
    loadData()
  }, [v, highlight, dispatch, t])

  // 4. CLEAN EVENT HANDLERS
  const handleSelectVocabulary = (id: number) => updateParams({ v: id })
  const handleSearch = () => updateParams({ showSearch: true })
  const handleCloseSearch = () => updateParams({ showSearch: false })

  return (
    <div style={{ display: 'flex' }} className={isSearchShown ? 'mobile-search' : ''}>
      <SearchPanel onCloseSearch={handleCloseSearch} />
      <VocabularyPanel
        onSelectVocabulary={handleSelectVocabulary}
        onSearch={handleSearch}
      />
    </div>
  )
}

export default LandingPage